import React from 'react';
import { formatINR } from './billFormatUtils';

function formatAssignedLines(lineIndices, itemsSrc, rowsSrc) {
  if (!Array.isArray(lineIndices) || lineIndices.length === 0) return '—';
  return lineIndices
    .map((li) => {
      const it = itemsSrc[li];
      const raw = rowsSrc[li] || {};
      const desc = (it && String(it.description || it.itemName || '').trim())
        ? (it.description || it.itemName)
        : `Line ${li + 1}`;
      let m = '';
      if (raw.qtyDisplay) m = String(raw.qtyDisplay);
      else if (raw.qtyExpr && String(raw.qtyExpr).trim()) m = String(raw.qtyExpr).trim();
      const q = parseFloat(raw.qty) || 0;
      const u = raw.unit || raw.quantityMeasurement || '';
      if (!m && q > 0) m = `${q}${u ? ` ${u}` : ''}`;
      return m ? `${desc} (${m})` : desc;
    })
    .join('; ');
}

export default function QuotationSubPartyBreakdown({
  breakdown,
  billData,
  payableAmount,
  variant = 'standard',
}) {
  if (!Array.isArray(breakdown) || breakdown.length === 0) return null;

  const itemsSrc = billData.items || [];
  const rowsSrc = billData.rows || [];
  const sum = breakdown.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const ok = Math.abs(sum - payableAmount) < 0.02;

  const boxClass =
    variant === 'colored'
      ? 'mb-4 p-3 border-l-4 border-violet-200 bg-violet-50/50 rounded-r text-xs'
      : 'mb-2 p-2 border border-gray-200 rounded-md bg-slate-50 text-xs';
  const titleClass =
    variant === 'colored' ? 'font-semibold text-violet-700 mb-1' : 'font-semibold text-gray-700 mb-1';
  const matchClass = ok
    ? variant === 'colored' ? 'text-emerald-700' : 'text-green-700'
    : variant === 'colored' ? 'text-amber-800' : 'text-amber-800';

  return (
    <div className={boxClass}>
      <div className={titleClass}>Sub party breakdown (reference)</div>
      <table className="w-full table-fixed border-collapse">
        <colgroup>
          <col className="w-[22%]" />
          <col className="w-[58%]" />
          <col className="w-[20%]" />
        </colgroup>
        <thead>
          <tr className="text-gray-600 border-b border-gray-200">
            <th className="text-left py-1 pr-2 font-medium">Sub party</th>
            <th className="text-left py-1 pr-2 font-medium max-w-0 break-words [overflow-wrap:anywhere] [word-break:break-word] [hyphens:auto]">
              Assigned lines
            </th>
            <th className="text-right py-1 font-medium">Amount</th>
          </tr>
        </thead>
        <tbody>
          {breakdown.map((row, i) => (
            <tr key={i} className="border-b border-gray-100">
              <td className="py-1 pr-2 align-top">{row.name || '—'}</td>
              <td className="py-1 pr-2 text-gray-600 align-top text-[0.7rem] leading-snug max-w-0 break-words [overflow-wrap:anywhere] [word-break:break-word] [hyphens:auto]">
                {formatAssignedLines(row.lineIndices, itemsSrc, rowsSrc)}
              </td>
              <td className="py-1 text-right tabular-nums align-top">₹{formatINR(row.amount)}</td>
            </tr>
          ))}
          <tr className="font-semibold">
            <td className="pt-1" colSpan={2}>Total</td>
            <td className="pt-1 text-right tabular-nums">₹{formatINR(sum)}</td>
          </tr>
        </tbody>
      </table>
      <p className={`mt-1 text-[10px] ${matchClass}`}>
        {ok
          ? `Matches payable amount (₹${formatINR(payableAmount)}).`
          : `Sum does not match payable (₹${formatINR(payableAmount)}).`}
      </p>
    </div>
  );
}
